import { useMemo, useState } from "react";

import { ChipList, StatusBadge } from "@/app/library/components/molecules";
import {
  getMockTemplatePackCoreVertical,
  getMockTemplatePackVersion,
  isMockTemplatePackAllowedForPlan,
  mockAccessPresets,
  mockTemplatePacks,
} from "@/app/library/data_sources/mock";
import type { Tenant, TenantPlan, TenantVertical } from "@/app/tenants/utils";
import { Button } from "@/core/ui/atoms";
import {
  adminInputClasses,
  adminSelectClasses,
  AdminMiniList,
  AdminMiniRow,
  AdminPanel,
  AdminStatCard,
  Banner,
} from "@/core/ui/molecules";

type TenantSetupDialogProps = {
  readonly onClose: () => void;
  readonly onCreate: (tenant: Tenant) => void;
};

const plans = ["Go", "Growth", "Scale", "Enterprise"] as const;
const verticals = [
  "Generic",
  "HVAC",
  "Facility Mgmt",
  "Pest Control",
  "Telco",
] as const;

export function TenantSetupDialog({
  onClose,
  onCreate,
}: TenantSetupDialogProps) {
  const [name, setName] = useState("");
  const [owner, setOwner] = useState("");
  const [plan, setPlan] = useState<TenantPlan>("Go");
  const [vertical, setVertical] = useState<TenantVertical>("Generic");
  const [billing, setBilling] = useState<Tenant["billing"]>("Stripe");
  const availablePacks = useMemo(
    () => mockTemplatePacks.filter((pack) => pack.status === "active"),
    [],
  );
  const allowedPacks = availablePacks.filter((pack) =>
    isMockTemplatePackAllowedForPlan(pack, plan),
  );
  const selectedPresets = mockAccessPresets.filter((preset) =>
    preset.plans.includes(plan),
  );
  const selectedPackNames = allowedPacks
    .filter((pack) => {
      const coreVertical = getMockTemplatePackCoreVertical(pack);
      return (
        coreVertical === "General" ||
        coreVertical === vertical ||
        pack.verticals?.some((item) => item.name === vertical)
      );
    })
    .map((pack) => pack.name ?? "");
  const counts = selectedPackNames.reduce(
    (acc, packName) => {
      const pack = availablePacks.find((item) => item.name === packName);
      const templates = pack?.templates ?? [];
      return {
        forms:
          acc.forms + templates.filter((item) => item.type === "form").length,
        media:
          acc.media + templates.filter((item) => item.type === "media").length,
        models:
          acc.models + templates.filter((item) => item.type === "model").length,
        templates: acc.templates + templates.length,
      };
    },
    { forms: 0, media: 0, models: 0, templates: 0 },
  );

  return (
    <div className="flex max-h-[calc(100vh-7.5rem)] w-[1180px] max-w-[96vw] flex-col overflow-hidden rounded-dialog bg-surface shadow-dialog">
      <header className="flex shrink-0 items-start justify-between gap-4 border-b border-border px-5 py-5">
        <div>
          <h2 className="text-lg font-extrabold text-ink">Create Tenant</h2>
          <p className="mt-1 text-[13px] text-ink-muted">
            5-step setup in one functional demo screen. Plan defines allowed
            packs; Super Admin selects which allowed packs to install.
          </p>
        </div>
        <button
          className="text-2xl text-ink-subtle"
          onClick={onClose}
          type="button"
        >
          ×
        </button>
      </header>
      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5">
        <div className="mb-4 grid grid-cols-1 gap-1.5 md:grid-cols-5">
          {[
            "1. Basic Info",
            "2. Plan & Vertical",
            "3. Template Packs",
            "4. Access Presets",
            "5. Review",
          ].map((step, index) => (
            <div
              className={`rounded-control border px-2 py-2 text-center text-xs font-extrabold ${
                index < 2
                  ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                  : index < 4
                    ? "border-orange-200 bg-orange-soft text-cta"
                    : "border-border bg-panel-muted text-ink-muted"
              }`}
              key={step}
            >
              {step}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <Field
            label="Company name"
            onChange={setName}
            placeholder="Example: AC Pro Miami"
            value={name}
          />
          <Field
            label="Owner email"
            onChange={setOwner}
            placeholder="owner@company.com"
            type="email"
            value={owner}
          />
          <SelectField
            label="Plan"
            onChange={(value) => setPlan(value as TenantPlan)}
            options={plans}
            value={plan}
          />
          <SelectField
            label="Vertical"
            onChange={(value) => setVertical(value as TenantVertical)}
            options={verticals}
            value={vertical}
          />
          <Field label="Country" onChange={() => undefined} value="US" />
          <SelectField
            label="Billing"
            onChange={(value) => setBilling(value as Tenant["billing"])}
            options={["Stripe", "Manual / Invoice", "Exempt"]}
            value={billing}
          />
        </div>

        <h3 className="mb-2 mt-5 text-[15px] font-extrabold text-ink">
          Template Packs
        </h3>
        <div className="overflow-hidden rounded-control border border-border">
          <div className="grid grid-cols-[42px_1.6fr_1fr_1fr_1fr] gap-3 border-b border-border bg-panel-muted px-3 py-2.5 text-[11px] font-extrabold uppercase tracking-[0.06em] text-ink-subtle">
            <div />
            <div>Pack</div>
            <div>Contents</div>
            <div>Vertical</div>
            <div>Plan availability</div>
          </div>
          {availablePacks.map((pack) => {
            const allowed = isMockTemplatePackAllowedForPlan(pack, plan);
            const coreVertical = getMockTemplatePackCoreVertical(pack);
            const checked = selectedPackNames.includes(pack.name ?? "");

            return (
              <div
                className={`grid grid-cols-[42px_1.6fr_1fr_1fr_1fr] items-center gap-3 border-b border-border px-3 py-2.5 last:border-b-0 ${
                  !allowed ? "bg-gray-50 opacity-60" : ""
                }`}
                key={pack.name}
              >
                <input checked={checked} disabled type="checkbox" />
                <span>
                  <strong>{pack.name}</strong>
                  <span className="block text-xs text-ink-muted">
                    {pack.description} · {getMockTemplatePackVersion(pack)}
                  </span>
                </span>
                <Contents pack={pack} />
                <ChipList values={pack.verticals?.map((item) => item.name)} />
                <StatusBadge
                  status={
                    allowed
                      ? coreVertical === vertical
                        ? "Core required"
                        : "Allowed"
                      : "Not in plan"
                  }
                />
              </div>
            );
          })}
        </div>
        <Banner tone="warning">
          <strong>Rule:</strong> plans allow packs. Tenant Setup installs only
          selected allowed packs. Packs not defined in the selected plan are
          visible for reference but disabled unless plan availability is changed
          in Plan Editor.
        </Banner>

        <h3 className="mb-2 mt-5 text-[15px] font-extrabold text-ink">
          Access Presets
        </h3>
        <Banner>
          <strong>Access setup:</strong> roles are fixed in code: Owner,
          Supervisor, Member, Viewer. Super Admin selects Permission Profile
          Presets; Tenant Setup installs tenant-level copies.
        </Banner>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {mockAccessPresets.map((preset) => {
            const allowed = preset.plans.includes(plan);
            return (
              <label
                className={`rounded-control border border-border p-3 ${
                  !allowed ? "bg-gray-50 opacity-60" : "bg-surface"
                }`}
                key={preset.id}
              >
                <div className="flex items-start gap-2">
                  <input checked={allowed} disabled type="checkbox" />
                  <span>
                    <strong>{preset.name}</strong>
                    <span className="block text-xs text-ink-muted">
                      Role: {preset.role} · {preset.status}
                    </span>
                    <span className="mt-1.5 block">
                      <ChipList values={preset.plans} />
                    </span>
                  </span>
                </div>
              </label>
            );
          })}
        </div>

        <h3 className="mb-2 mt-5 text-[15px] font-extrabold text-ink">
          Review & Create · Setup Summary
        </h3>
        <AdminPanel className="p-[18px]">
          <AdminMiniList>
            <AdminMiniRow>
              <strong>Tenant</strong>
              <span>{name || "AC Pro Miami"}</span>
            </AdminMiniRow>
            <AdminMiniRow>
              <strong>Plan + Vertical</strong>
              <span>
                {plan} · {vertical}
              </span>
            </AdminMiniRow>
            <AdminMiniRow>
              <strong>Initial workspace</strong>
              <span>Main</span>
            </AdminMiniRow>
            <AdminMiniRow>
              <strong>Owner</strong>
              <span>{owner || "owner@company.com"}</span>
            </AdminMiniRow>
            <AdminMiniRow>
              <strong>Access presets</strong>
              <ChipList values={selectedPresets.map((preset) => preset.name)} />
            </AdminMiniRow>
            <AdminMiniRow>
              <strong>Template packs</strong>
              <ChipList values={selectedPackNames} />
            </AdminMiniRow>
          </AdminMiniList>
        </AdminPanel>
        <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-3 xl:grid-cols-6">
          <AdminStatCard label="Packs" value={selectedPackNames.length} />
          <AdminStatCard label="Templates" value={counts.templates} />
          <AdminStatCard label="Models" value={counts.models} />
          <AdminStatCard label="Forms" value={counts.forms} />
          <AdminStatCard label="Media" value={counts.media} />
          <AdminStatCard
            label="Access Presets"
            value={selectedPresets.length}
          />
        </div>
        <Banner>
          <strong>Rollback rule:</strong> if workspace, owner, access presets,
          billing, invite or template installation fails, Tenant Setup rolls
          back the full operation. No partial tenant is allowed.
        </Banner>
      </div>
      <footer className="flex shrink-0 justify-end gap-2 border-t border-border bg-panel-muted px-5 py-3.5">
        <Button onClick={onClose}>Cancel</Button>
        <Button
          disabled={!name.trim() || !owner.trim()}
          onClick={() =>
            onCreate({
              billing,
              code: slugify(name),
              country: "US",
              countryCode: "US",
              createdAt: "Jun 1, 2026",
              createdBy: "Super Admin",
              id: slugify(name),
              lastLogin: "never",
              name,
              owner,
              ownerName: owner,
              plan,
              planVersion: 1,
              product: plan === "Enterprise" ? "Enterprise" : "SaaS",
              status: "trial",
              trialDays: 14,
              users: 1,
              vertical,
            })
          }
          variant="primary"
        >
          Create Tenant
        </Button>
      </footer>
    </div>
  );
}

function Field({
  label,
  onChange,
  placeholder,
  type = "text",
  value,
}: {
  readonly label: string;
  readonly onChange: (value: string) => void;
  readonly placeholder?: string;
  readonly type?: string;
  readonly value: string;
}) {
  return (
    <label>
      <span className="mb-1.5 block text-xs font-extrabold text-ink-muted">
        {label}
      </span>
      <input
        className={adminInputClasses}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        type={type}
        value={value}
      />
    </label>
  );
}

function SelectField({
  label,
  onChange,
  options,
  value,
}: {
  readonly label: string;
  readonly onChange: (value: string) => void;
  readonly options: readonly string[];
  readonly value: string;
}) {
  return (
    <label>
      <span className="mb-1.5 block text-xs font-extrabold text-ink-muted">
        {label}
      </span>
      <select
        className={adminSelectClasses}
        onChange={(event) => onChange(event.target.value)}
        value={value}
      >
        {options.map((option) => (
          <option key={option}>{option}</option>
        ))}
      </select>
    </label>
  );
}

function Contents({
  pack,
}: {
  readonly pack: (typeof mockTemplatePacks)[number];
}) {
  const templates = pack.templates ?? [];
  return (
    <span className="flex flex-wrap gap-1">
      <span className="rounded-full border border-border bg-panel-muted px-2 py-0.5 text-[11px] font-bold text-ink-muted">
        Forms {templates.filter((item) => item.type === "form").length}
      </span>
      <span className="rounded-full border border-border bg-panel-muted px-2 py-0.5 text-[11px] font-bold text-ink-muted">
        Media {templates.filter((item) => item.type === "media").length}
      </span>
      <span className="rounded-full border border-border bg-panel-muted px-2 py-0.5 text-[11px] font-bold text-ink-muted">
        Models {templates.filter((item) => item.type === "model").length}
      </span>
    </span>
  );
}

function slugify(value: string) {
  return (
    value
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 40) || "tenant"
  );
}
